import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import api from "@/lib/api";
import VisitRemindersWidget from "@/components/ehr/VisitRemindersWidget";
import {
  Video, Phone, PhoneOff, MessageCircle, Send, AlertTriangle,
  Heart, Star, ThumbsUp, User, Users, Clock, Check, X,
  Loader2, ChevronRight, Bell, Volume2, VolumeX, Shield,
  Smile, Award, Flag, FileText, RefreshCw, Calendar
} from "lucide-react";

const COMPLAINT_TYPES = [
  { value: "service_quality", label: "Service Quality", icon: Star },
  { value: "staff_behavior", label: "Staff Behavior", icon: User },
  { value: "scheduling", label: "Scheduling Issues", icon: Clock },
  { value: "billing", label: "Billing Concern", icon: FileText },
  { value: "communication", label: "Communication", icon: MessageCircle },
  { value: "safety_concern", label: "Safety Concern", icon: Shield },
  { value: "other", label: "Other", icon: Flag }
];

const RECOGNITION_TYPES = [
  { value: "excellent_care", label: "Excellent Care", icon: Heart, color: "bg-red-100 text-red-700" },
  { value: "compassion", label: "Compassion", icon: Heart, color: "bg-pink-100 text-pink-700" },
  { value: "going_extra_mile", label: "Going Extra Mile", icon: Star, color: "bg-amber-100 text-amber-700" },
  { value: "communication", label: "Great Communication", icon: MessageCircle, color: "bg-blue-100 text-blue-700" },
  { value: "professionalism", label: "Professionalism", icon: Award, color: "bg-purple-100 text-purple-700" },
  { value: "comfort_provided", label: "Comfort Provided", icon: Smile, color: "bg-green-100 text-green-700" },
  { value: "family_support", label: "Family Support", icon: Users, color: "bg-teal-100 text-teal-700" }
];

export default function PatientPortalPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("messages");
  const [loading, setLoading] = useState(false);
  
  // Telehealth State
  const [pendingCall, setPendingCall] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [callMuted, setCallMuted] = useState(false);
  const [callStatus, setCallStatus] = useState(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  
  // Messaging State
  const [coreTeam, setCoreTeam] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  
  // Complaint State
  const [showComplaintDialog, setShowComplaintDialog] = useState(false);
  const [complaintForm, setComplaintForm] = useState({
    complaint_type: "service_quality",
    priority: "medium",
    subject: "",
    description: "",
    is_anonymous: false
  });
  const [myComplaints, setMyComplaints] = useState([]);
  
  // Recognition State
  const [showRecognitionDialog, setShowRecognitionDialog] = useState(false);
  const [recognitionForm, setRecognitionForm] = useState({
    staff_id: "",
    staff_name: "",
    recognition_type: "excellent_care",
    message: "",
    allow_sharing: true
  });
  
  const loadCoreTeam = useCallback(async () => {
    try {
      const res = await api.get("/patient-portal/messages/core-team");
      setCoreTeam(res.data.team || []);
    } catch (error) {
      console.error("Error loading core team:", error);
    }
  }, []);
  
  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get("/patient-portal/messages/conversations");
      setConversations(res.data.conversations || []);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  }, []);
  
  const loadPendingCalls = useCallback(async () => {
    try {
      const res = await api.get("/patient-portal/telehealth/pending-calls");
      if (res.data.calls?.length > 0) {
        setPendingCall(res.data.calls[0]);
      }
    } catch (error) {
      console.error("Error checking calls:", error);
    }
  }, []);
  
  const loadComplaints = useCallback(async () => {
    try {
      const res = await api.get("/patient-portal/complaints/my-submissions");
      setMyComplaints(res.data.complaints || []);
    } catch (error) {
      console.error("Error loading complaints:", error);
    }
  }, []);

  useEffect(() => {
    loadCoreTeam();
    loadConversations();
    loadPendingCalls();
    loadComplaints();
    
    // Poll for incoming calls
    const pollInterval = setInterval(loadPendingCalls, 10000);
    return () => clearInterval(pollInterval);
  }, [loadCoreTeam, loadConversations, loadPendingCalls, loadComplaints]);

  const answerCall = async (callId) => {
    try {
      await api.post(`/patient-portal/telehealth/calls/${callId}/answer`);
      setInCall(true);
      setPendingCall(null);
      setCallStatus("connected");
      toast.success("Call connected");
      
      // Initialize simple WebRTC (peer-to-peer)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Media error:", err);
        toast.error("Could not access camera/microphone");
      }
    } catch (error) {
      toast.error("Failed to answer call");
    }
  };

  const declineCall = async (callId) => {
    try {
      await api.post(`/patient-portal/telehealth/calls/${callId}/decline`);
      setPendingCall(null);
      toast.info("Call declined");
    } catch (error) {
      toast.error("Failed to decline call");
    }
  };

  const endCall = async () => {
    setInCall(false);
    setCallStatus(null);
    
    // Stop media tracks
    if (localVideoRef.current?.srcObject) {
      localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    toast.info("Call ended");
  };

  const loadMessages = async (conversationId) => {
    try {
      const res = await api.get(`/patient-portal/messages/conversation/${conversationId}`);
      setMessages(res.data.messages || []);
      setSelectedConversation(conversationId);
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      await api.post("/patient-portal/messages/send", {
        conversation_id: selectedConversation,
        content: newMessage.trim()
      });
      setNewMessage("");
      loadMessages(selectedConversation);
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async (staffId) => {
    try {
      const res = await api.post("/patient-portal/messages/start", {
        recipient_id: staffId
      });
      loadConversations();
      loadMessages(res.data.conversation_id);
      toast.success("Conversation started");
    } catch (error) {
      toast.error("Failed to start conversation");
    }
  };

  const submitComplaint = async () => {
    if (!complaintForm.subject || !complaintForm.description) {
      toast.error("Please fill in subject and description");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/patient-portal/complaints/submit", complaintForm);
      toast.success("Complaint submitted. We take all feedback seriously.");
      setShowComplaintDialog(false);
      setComplaintForm({
        complaint_type: "service_quality",
        priority: "medium",
        subject: "",
        description: "",
        is_anonymous: false
      });
      loadComplaints();
    } catch (error) {
      toast.error("Failed to submit complaint");
    } finally {
      setLoading(false);
    }
  };

  const submitRecognition = async () => {
    if (!recognitionForm.staff_id || !recognitionForm.message) {
      toast.error("Please select a staff member and write a message");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/patient-portal/recognition/submit", recognitionForm);
      toast.success("Thank you for recognizing your care team!");
      setShowRecognitionDialog(false);
      setRecognitionForm({
        staff_id: "",
        staff_name: "",
        recognition_type: "excellent_care",
        message: "",
        allow_sharing: true
      });
    } catch (error) {
      toast.error("Failed to submit recognition");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700">Pending Review</Badge>;
      case "in_progress":
        return <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>;
      case "resolved":
        return <Badge className="bg-green-100 text-green-700">Resolved</Badge>;
      case "closed":
        return <Badge className="bg-gray-100 text-gray-700">Closed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "??";
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="patient-portal-page">
      {/* Incoming Call Alert */}
      {pendingCall && !inCall && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <Card className="w-full max-w-md mx-4 animate-pulse-slow">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <Phone className="w-10 h-10 text-green-600 animate-bounce" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Incoming Call</h2>
              <p className="text-lg text-muted-foreground mb-1">{pendingCall.caller_name}</p>
              <p className="text-sm text-muted-foreground mb-6">{pendingCall.purpose}</p>
              <div className="flex justify-center gap-4">
                <Button
                  variant="destructive"
                  size="lg"
                  className="rounded-full w-16 h-16"
                  onClick={() => declineCall(pendingCall.id)}
                  data-testid="decline-call-btn"
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
                <Button
                  size="lg"
                  className="rounded-full w-16 h-16 bg-green-600 hover:bg-green-700"
                  onClick={() => answerCall(pendingCall.id)}
                  data-testid="answer-call-btn"
                >
                  <Phone className="w-6 h-6" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* In-Call View */}
      {inCall && (
        <div className="fixed inset-0 z-50 bg-slate-900 flex flex-col">
          <div className="flex-1 relative">
            {/* Remote Video (Full Screen) */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            {/* Local Video (Picture-in-Picture) */}
            <div className="absolute bottom-4 right-4 w-32 h-24 rounded-lg overflow-hidden border-2 border-white shadow-lg">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
            </div>
            {/* Call Info */}
            <div className="absolute top-4 left-4 text-white">
              <p className="text-lg font-medium">Connected</p>
              <p className="text-sm opacity-80">Telehealth Visit</p>
            </div>
          </div>
          {/* Call Controls */}
          <div className="bg-slate-800 p-6 flex justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              className="rounded-full w-14 h-14 bg-slate-700 border-slate-600 hover:bg-slate-600"
              onClick={() => setCallMuted(!callMuted)}
            >
              {callMuted ? <VolumeX className="w-6 h-6 text-white" /> : <Volume2 className="w-6 h-6 text-white" />}
            </Button>
            <Button
              variant="destructive"
              size="lg"
              className="rounded-full w-14 h-14"
              onClick={endCall}
              data-testid="end-call-btn"
            >
              <PhoneOff className="w-6 h-6" />
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Heart className="w-8 h-8" />
                <h1 className="text-3xl font-bold">Patient Portal</h1>
              </div>
              <p className="text-emerald-100">
                Connect with your care team, share feedback, and manage your care
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowRecognitionDialog(true)}
                data-testid="recognize-team-btn"
              >
                <Star className="w-4 h-4 mr-2" />
                Recognize Care Team
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-lg mb-8">
            <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="tab-messages">
              <MessageCircle className="w-4 h-4" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="visits" className="flex items-center gap-2" data-testid="tab-visits">
              <Calendar className="w-4 h-4" />
              Visits
            </TabsTrigger>
            <TabsTrigger value="telehealth" className="flex items-center gap-2" data-testid="tab-telehealth">
              <Video className="w-4 h-4" />
              Telehealth
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex items-center gap-2" data-testid="tab-feedback">
              <Flag className="w-4 h-4" />
              Feedback
            </TabsTrigger>
          </TabsList>

          {/* MESSAGES TAB */}
          <TabsContent value="messages" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Conversations List */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Your Care Team</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {coreTeam.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => startNewConversation(member.id)}
                      data-testid={`team-member-${member.id}`}
                    >
                      <Avatar>
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.role}</p>
                      </div>
                      {member.is_primary && (
                        <Badge variant="outline" className="text-xs">Primary</Badge>
                      )}
                    </div>
                  ))}
                  
                  {conversations.length > 0 && (
                    <>
                      <div className="border-t my-4" />
                      <p className="text-sm text-muted-foreground font-medium">Recent Conversations</p>
                      {conversations.slice(0, 5).map((conv) => (
                        <div
                          key={conv.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedConversation === conv.id ? "bg-primary/10" : "hover:bg-slate-50"
                          }`}
                          onClick={() => loadMessages(conv.id)}
                        >
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="text-xs">
                              {getInitials(conv.participant_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{conv.participant_name}</p>
                            <p className="text-xs text-muted-foreground truncate">{conv.last_message}</p>
                          </div>
                          {conv.unread_count > 0 && (
                            <Badge className="bg-primary">{conv.unread_count}</Badge>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Message Thread */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {selectedConversation ? "Conversation" : "Select a team member to message"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedConversation ? (
                    <div className="space-y-4">
                      <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-4">
                          {messages.map((msg, idx) => (
                            <div
                              key={idx}
                              className={`flex ${msg.is_from_patient ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-lg p-3 ${
                                  msg.is_from_patient
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-slate-100"
                                }`}
                              >
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${
                                  msg.is_from_patient ? "text-primary-foreground/70" : "text-muted-foreground"
                                }`}>
                                  {new Date(msg.sent_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                      
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                          data-testid="message-input"
                        />
                        <Button onClick={sendMessage} disabled={sending} data-testid="send-message-btn">
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Select a care team member to start messaging</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* VISITS TAB */}
          <TabsContent value="visits" className="space-y-6">
            <VisitRemindersWidget />
          </TabsContent>

          {/* TELEHEALTH TAB */}
          <TabsContent value="telehealth" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Telehealth Visits
                </CardTitle>
                <CardDescription>
                  Your care team may initiate video calls for check-ins and consultations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Video className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Ready for Video Visits</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mb-6">
                    When your care team initiates a video call, you'll see an incoming call notification.
                    Make sure your camera and microphone are enabled.
                  </p>
                  <div className="flex justify-center gap-4">
                    <Button variant="outline" onClick={loadPendingCalls} data-testid="check-calls-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Check for Calls
                    </Button>
                  </div>
                  
                  {pendingCall && (
                    <div className="mt-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <Bell className="w-6 h-6 text-green-600 mx-auto mb-2" />
                      <p className="font-medium text-green-800">Incoming Call from {pendingCall.caller_name}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FEEDBACK TAB */}
          <TabsContent value="feedback" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Submit Complaint */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flag className="w-5 h-5" />
                    Share a Concern
                  </CardTitle>
                  <CardDescription>
                    We value your feedback and take all concerns seriously
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={() => setShowComplaintDialog(true)} data-testid="submit-complaint-btn">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Report an Issue
                  </Button>
                  
                  {myComplaints.length > 0 && (
                    <div className="mt-6 space-y-3">
                      <h4 className="text-sm font-medium text-muted-foreground">Your Submissions</h4>
                      {myComplaints.map((complaint) => (
                        <div key={complaint.id} className="p-3 border rounded-lg" data-testid={`complaint-${complaint.id}`}>
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-sm">{complaint.subject}</p>
                            {getStatusBadge(complaint.status)}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Submitted {new Date(complaint.submitted_at).toLocaleDateString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recognize Care Team */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    Recognize Your Care Team
                  </CardTitle>
                  <CardDescription>
                    Let us know when a team member goes above and beyond
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" onClick={() => setShowRecognitionDialog(true)} data-testid="recognize-btn">
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Send Recognition
                  </Button>
                  
                  <div className="mt-6 flex flex-wrap gap-2">
                    {RECOGNITION_TYPES.slice(0, 4).map((type) => (
                      <Badge key={type.value} className={type.color}>
                        <type.icon className="w-3 h-3 mr-1" />
                        {type.label}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Complaint Dialog */}
      <Dialog open={showComplaintDialog} onOpenChange={setShowComplaintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Report a Concern</DialogTitle>
            <DialogDescription>
              Your feedback helps us improve our services
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Type of Concern</Label>
              <Select
                value={complaintForm.complaint_type}
                onValueChange={(v) => setComplaintForm({ ...complaintForm, complaint_type: v })}
              >
                <SelectTrigger data-testid="complaint-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPLAINT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Subject</Label>
              <Input
                placeholder="Brief summary..."
                value={complaintForm.subject}
                onChange={(e) => setComplaintForm({ ...complaintForm, subject: e.target.value })}
                data-testid="complaint-subject-input"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Please describe your concern in detail..."
                value={complaintForm.description}
                onChange={(e) => setComplaintForm({ ...complaintForm, description: e.target.value })}
                className="min-h-[120px]"
                data-testid="complaint-description-textarea"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="anonymous"
                checked={complaintForm.is_anonymous}
                onChange={(e) => setComplaintForm({ ...complaintForm, is_anonymous: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="anonymous" className="text-sm">Submit anonymously</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplaintDialog(false)}>Cancel</Button>
            <Button onClick={submitComplaint} disabled={loading} data-testid="submit-complaint-confirm-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recognition Dialog */}
      <Dialog open={showRecognitionDialog} onOpenChange={setShowRecognitionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Recognize Your Care Team
            </DialogTitle>
            <DialogDescription>
              Share what made a difference in your care
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Team Member</Label>
              <Select
                value={recognitionForm.staff_id}
                onValueChange={(v) => {
                  const member = coreTeam.find(m => m.id === v);
                  setRecognitionForm({
                    ...recognitionForm,
                    staff_id: v,
                    staff_name: member?.name || ""
                  });
                }}
              >
                <SelectTrigger data-testid="recognition-staff-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {coreTeam.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} ({member.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Type of Recognition</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {RECOGNITION_TYPES.map((type) => (
                  <Button
                    key={type.value}
                    variant={recognitionForm.recognition_type === type.value ? "default" : "outline"}
                    size="sm"
                    className="justify-start"
                    onClick={() => setRecognitionForm({ ...recognitionForm, recognition_type: type.value })}
                  >
                    <type.icon className="w-4 h-4 mr-2" />
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label>Your Message</Label>
              <Textarea
                placeholder="Share your experience and what made a difference..."
                value={recognitionForm.message}
                onChange={(e) => setRecognitionForm({ ...recognitionForm, message: e.target.value })}
                className="min-h-[100px]"
                data-testid="recognition-message-textarea"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="allow-sharing"
                checked={recognitionForm.allow_sharing}
                onChange={(e) => setRecognitionForm({ ...recognitionForm, allow_sharing: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="allow-sharing" className="text-sm">Allow sharing with the team</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecognitionDialog(false)}>Cancel</Button>
            <Button onClick={submitRecognition} disabled={loading} data-testid="submit-recognition-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Heart className="w-4 h-4 mr-2" />}
              Send Recognition
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
